import React, { CSSProperties, ReactNode } from "react";
import { Typography } from "antd";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import OpenInNewTabIcon from "../../../svgs/open-in-new-tab.svg?react";
import { WithCopyable } from "../Copyable";
import {
  CopyEmoji,
  CustomerAttributeIconWrapper,
  CustomerAttributeLabel,
  CustomerAttributeLabelContainer,
  CustomerAttributeValue,
  EditOutlinedIcon,
  InspectLink,
} from "./styledComponents";

const renderEditTooltip = (props: any) => (
  <Tooltip id="button-tooltip-edit" {...props}>
    Click to edit
  </Tooltip>
);

export type CustomerAttributeProps = {
  label: string;
  children: ReactNode;
  isCopyable?: boolean;
  onEdit?: any;
  onInspect?: any;
  icon?: React.ReactNode;
  withFixedIcon?: boolean;
  dataTest?: string;
  changeLog?: ReactNode;
  className?: string;
  style?: CSSProperties;
  // Compact one-line variant: label column, then value, edit icon and log.
  inline?: boolean;
};

const CustomerAttribute = ({
  label,
  children,
  isCopyable,
  onEdit,
  onInspect,
  icon,
  withFixedIcon,
  dataTest,
  changeLog,
  className,
  style,
  inline,
}: CustomerAttributeProps) => {
  const editButton = typeof onEdit === "function" && (
    <OverlayTrigger placement="top" overlay={renderEditTooltip}>
      <CopyEmoji
        onClick={onEdit}
        data-test={dataTest ? dataTest + "Icon" : "fieldIcon"}
      >
        <EditOutlinedIcon />
      </CopyEmoji>
    </OverlayTrigger>
  );

  if (inline) {
    return (
      <div
        data-test={dataTest}
        className={className}
        style={{
          display: "grid",
          gridTemplateColumns: "240px 1fr",
          gridGap: 8,
          padding: "2px 0",
          alignItems: "center",
          ...style,
        }}
      >
        <Typography.Text type="secondary">{label}</Typography.Text>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {children || "-"}
          {editButton}
          {changeLog}
        </div>
      </div>
    );
  }

  return (
    <div data-test={dataTest} className={className} style={style}>
      <CustomerAttributeLabelContainer>
        <CustomerAttributeLabel>{label}</CustomerAttributeLabel>
        {editButton}
        {icon && (
          <CustomerAttributeIconWrapper>{icon}</CustomerAttributeIconWrapper>
        )}
        {changeLog}
      </CustomerAttributeLabelContainer>
      <CustomerAttributeValue>
        {children ? (
          <WithCopyable copyable={isCopyable} withFixedIcon={withFixedIcon}>
            {children}
          </WithCopyable>
        ) : (
          "-"
        )}
      </CustomerAttributeValue>
      {typeof onInspect === "function" && (
        <InspectLink onClick={onInspect}>
          <OpenInNewTabIcon />
          <span>Open in new tab</span>
        </InspectLink>
      )}
    </div>
  );
};

export default CustomerAttribute;
